/**
 * Benchmarks — one scrolling page covering both:
 *   1. Per-asset capacity-factor bars (every portfolio asset vs its peers)
 *   2. Time-series chart for one picked asset (CF / generation MWh/MW /
 *      revenue $/MW / curtailment, target vs each peer)
 *
 * Generation (MWh ÷ MW) and Revenue (spot revenue ÷ MW) are both shown per
 * installed MW, never absolute — peers differ in size, so absolute MWh /
 * AUD only rank bigness. $/MW also rewards capture quality, not just volume.
 * Curtailment comes from the shared bid-based engine (Solar/Wind only) and
 * gets an extra cause-split strip: Economic vs Grid curtailment, same
 * labels and numbers as the dedicated Curtailment page.
 *
 * No view switcher — both sections live on the same page. The Edit
 * dialog reuses BenchmarkStep which pre-fills same-region/same-fuel
 * peer suggestions on open and exposes Refresh + Top N controls.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Select,
  MenuItem,
  Skeleton,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { useThemeColors } from '../theme/ThemeModeContext';
import { ENERGY_PALETTE } from './WaterfallChart';
import BenchmarkStep from './BenchmarkStep';
import PeriodPicker, { usePersistedPeriod } from './PeriodPicker';
import { periodApiParams } from '../utils/periodParams';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtMwhPerMw = (v) => (v == null ? '—' : `${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 1 })}`);
const fmtAudPerMw = (v) => (v == null ? '—' : `$${Math.round(Number(v)).toLocaleString('en-AU')}`);

// Both Generation and Revenue are normalised per installed MW, not shown
// absolute — peers differ in size, so absolute MWh / AUD only rank bigness.
// Daily chart values use the server's per-MW fields (generation_mwh_per_mw =
// specific yield, MWh ÷ capacity; revenue_aud_per_mw = spot revenue ÷
// capacity); the cards sum them into period totals comparable across the
// cohort. Curtailment (Solar/Wind only) is % of potential via agg 'ratio'
// (Σ curtailed ÷ Σ potential); lowerIsBetter flips the delta colour and
// rank direction.
const SERIES_METRICS = [
  { key: 'capacity_factor_pct',  label: 'Capacity Factor', unit: '%',      agg: 'avg', fmt: fmtPct },
  { key: 'generation_mwh_per_mw', label: 'Generation',     unit: 'MWh/MW', agg: 'sum',
    sub: 'specific yield ÷ capacity', fmt: fmtMwhPerMw },
  { key: 'revenue_aud_per_mw',   label: 'Revenue',         unit: '$/MW',   agg: 'sum',
    sub: 'period total ÷ capacity', fmt: fmtAudPerMw },
  { key: 'curtailment_pct',      label: 'Curtailment',     unit: '%',      agg: 'ratio',
    numKey: 'curtailed_mwh', denKey: 'potential_mwh', ratioScale: 100,
    sub: 'of potential generation', fmt: fmtPct,
    lowerIsBetter: true, intermittentOnly: true },
];

const BENCH_COLORS = ['#94a3b8', '#a78bfa', '#fb923c', '#22d3ee', '#facc15', '#f472b6', '#86efac', '#fda4af'];
const TARGET_COLOR = '#d98a2b';

function aggregate(series, metric) {
  if (!series || series.length === 0) return null;
  if (metric.agg === 'ratio') {
    let num = 0, den = 0, seen = false;
    series.forEach((r) => {
      const n = r[metric.numKey], d = r[metric.denKey];
      if (n == null || d == null) return;
      num += n; den += d; seen = true;
    });
    if (!seen || den === 0) return null;
    return (num / den) * (metric.ratioScale || 1);
  }
  const vals = series.map((r) => r[metric.key]).filter((v) => v != null);
  if (vals.length === 0) return null;
  if (metric.agg === 'sum') return vals.reduce((a, b) => a + b, 0);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}


// ── Time-series chart for one picked asset ───────────────────────────────

function SeriesMetricCard({ metric, value, selected, onClick }) {
  return (
    <Card
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: '1 1 200px', minWidth: 200, cursor: 'pointer',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: 'primary.light',
                     boxShadow: selected ? undefined : '0 2px 8px rgba(0,0,0,0.06)' },
        bgcolor: selected ? 'rgba(217,138,43,0.04)' : 'background.paper',
      }}
    >
      <CardContent sx={{ pb: '14px !important', pt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography sx={{
            fontSize: '0.66rem', color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontWeight: 700, flex: 1,
          }}>{metric.label}</Typography>
          <Chip size="small" label={metric.unit} sx={{ height: 18, fontSize: '0.62rem' }} />
        </Stack>
        <Typography sx={{
          fontSize: '1.35rem', fontWeight: 800,
          fontFamily: "'JetBrains Mono', monospace",
          lineHeight: 1.1, mb: 0.25,
        }}>{metric.fmt(value)}</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          {metric.sub || (metric.agg === 'sum' ? 'period total' : 'period average')}
        </Typography>
      </CardContent>
    </Card>
  );
}

function SeriesSection({ portfolioId, assetId, period, colors, targetFuel }) {
  const [series, setSeries] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(SERIES_METRICS[0].key);
  const [error, setError] = useState(null);

  // Fill the rest of the viewport below the chart container's top edge.
  // Same pattern as FacilityCovenants.js — measure-and-resize so the
  // chart grows on large screens instead of being pinned at 440px.
  const chartRef = useRef(null);
  const [chartH, setChartH] = useState(440);
  useEffect(() => {
    const compute = () => {
      const el = chartRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // Gutter below the chart: comparison-summary footer (~110px) +
      // card bottom padding (~20px) + page bottom padding (~20px) +
      // small safety margin so MUI chart legend doesn't push a scrollbar.
      const h = Math.max(280, Math.floor(window.innerHeight - top - 200));
      setChartH(h);
    };
    compute();
    window.addEventListener('resize', compute);
    const t = window.setTimeout(compute, 80);  // re-measure after layout
    return () => { window.removeEventListener('resize', compute); clearTimeout(t); };
  }, [selectedMetric, series]);

  useEffect(() => {
    if (!portfolioId || !assetId) return;
    let cancelled = false;
    setSeries(null);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/benchmark-series`, {
      // Standard PeriodPicker params (period code + explicit start/end
      // instants) — the backend honours the instants, so this page shows
      // the exact same window as the Technical tabs. Series is daily-grain
      // only, so no frequency param.
      params: { ...periodApiParams(period, { withFrequency: false }), asset_id: assetId },
    })
      .then((r) => { if (!cancelled) setSeries(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period]);

  const target = series?.target;
  const benchmarks = useMemo(() => series?.benchmarks || [], [series]);

  // Curtailment only exists for semi-scheduled (Solar/Wind) assets — the
  // server omits the fields otherwise, so hide the card instead of showing
  // a permanently empty chart. Gate on the asset's fuel type (known before
  // the series fetch) so the card is there from first render instead of
  // popping in when the data lands; the series-data check is only a
  // fallback for assets missing fuel_code. Falls back to the first metric
  // if the selected one disappears (e.g. switching solar → battery asset).
  const hasCurtailment = useMemo(() => (
    ['Solar', 'Wind'].includes(targetFuel) ||
    [target, ...benchmarks].some((a) =>
      (a?.series || []).some((r) => r.potential_mwh != null))
  ), [targetFuel, target, benchmarks]);
  const availableMetrics = SERIES_METRICS.filter(
    (m) => !m.intermittentOnly || hasCurtailment
  );
  const metric = availableMetrics.find((m) => m.key === selectedMetric) || availableMetrics[0];

  const xDates = useMemo(() => {
    const set = new Set();
    [target, ...benchmarks].forEach((a) => {
      (a?.series || []).forEach((r) => set.add(r.date));
    });
    return Array.from(set).sort();
  }, [target, benchmarks]);

  const seriesFor = (asset) => {
    if (!asset) return [];
    const byDate = new Map((asset.series || []).map((r) => [r.date, r[metric.key]]));
    return xDates.map((d) => {
      const v = byDate.get(d);
      return v == null ? null : v;
    });
  };

  // How many peers actually have dispatch data in this period?
  const peersWithData = benchmarks.filter((b) => (b.series || []).length > 0);
  const peersNoData   = benchmarks.filter((b) => (b.series || []).length === 0);
  const targetHasData = target && (target.series || []).length > 0;

  return (
    <Card sx={{ p: 2 }}>
      {/* Section header overline removed — the highlighted metric card
          tells the user which series is on the chart, so the duplicate
          "Series · X · daily" label was just wasted vertical space. */}

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {!assetId ? (
        <Alert severity="info">No asset selected.</Alert>
      ) : target && !targetHasData ? (
        <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed',
                    borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No dispatch data for <strong>{target.name}</strong> in this period.
            Try 90D or YTD, or pick a different asset.
          </Typography>
        </Box>
      ) : target && benchmarks.length === 0 ? (
        <Alert severity="info" icon={<CompareArrowsIcon fontSize="inherit" />}>
          No benchmarks set for <strong>{target.name}</strong>. Click
          "Edit benchmarks" above to pick peers — the chart needs at
          least one peer to compare against.
        </Alert>
      ) : (
        <>
          {/* Metric cards */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1.5 }}>
            {availableMetrics.map((m) => (
              <SeriesMetricCard key={m.key} metric={m}
                value={aggregate(target?.series, m)}
                selected={m.key === metric.key}
                onClick={() => setSelectedMetric(m.key)} />
            ))}
          </Stack>
          {peersNoData.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {peersNoData.length === benchmarks.length ? (
                <>None of the configured benchmarks have dispatch data in this
                period — try a longer window (90D / YTD) or pick different peers.</>
              ) : (
                <>{peersNoData.length} of {benchmarks.length} benchmark{benchmarks.length === 1 ? '' : 's'}{' '}
                ({peersNoData.map((b) => b.name).join(', ')}) have no data in this period.</>
              )}
            </Alert>
          )}
          <Box>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {series?.period?.label || 'Last 30 days'} · {peersWithData.length}/{benchmarks.length} benchmark{benchmarks.length === 1 ? '' : 's'} with data
              </Typography>
            </Stack>
            {!series ? (
              <Skeleton ref={chartRef} variant="rectangular" height={chartH}
                        sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
            ) : xDates.length === 0 ? (
              <Box ref={chartRef} sx={{ height: chartH, display: 'flex',
                                          alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.disabled">No data for {metric.label} in this period.</Typography>
              </Box>
            ) : (
              <Box ref={chartRef}>
              <LineChart
                height={chartH}
                xAxis={[{
                  data: xDates.map((d) => new Date(d)),
                  scaleType: 'time',
                  valueFormatter: (v) => {
                    const d = v instanceof Date ? v : new Date(v);
                    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
                  },
                }]}
                yAxis={[{ valueFormatter: (v) => metric.fmt(v) }]}
                series={[
                  { data: seriesFor(target), label: `${target.name} (this asset)`,
                    color: TARGET_COLOR, showMark: true, curve: 'monotoneX' },
                  ...benchmarks.map((b, i) => ({
                    data: seriesFor(b),
                    label: (b.series || []).length === 0 ? `${b.name} (no data)` : b.name,
                    color: BENCH_COLORS[i % BENCH_COLORS.length],
                    showMark: true, curve: 'monotoneX',
                  })),
                ]}
                margin={{ left: 70, right: 24, top: 16, bottom: 50 }}
                slotProps={{
                  legend: { direction: 'horizontal',
                            position: { vertical: 'bottom', horizontal: 'middle' } },
                }}
              />
              </Box>
            )}
          </Box>
          {metric.key === 'curtailment_pct' && (
            <CurtailmentSplitStrip target={target} peersWithData={peersWithData} />
          )}
          <ComparisonSummary
            metric={metric}
            target={target}
            peersWithData={peersWithData}
          />
        </>
      )}
    </Card>
  );
}


// ── Curtailment cause split (bid-based) ──────────────────────────────────
//
// Shown only while the Curtailment metric is selected. One stacked bar per
// asset with the period split from the shared bid-based engine: Economic
// curtailment (the unit offered above RRP and priced itself out, red) vs
// Grid curtailment (offered in-merit but constrained off, blue). Same
// palette, labels and daily numbers as the dedicated Curtailment page so
// the two screens read as one.

function CurtailmentSplitStrip({ target, peersWithData }) {
  const rows = [
    target ? { ...target, isTarget: true } : null,
    ...peersWithData,
  ]
    .filter(Boolean)
    .map((a) => {
      let pot = 0, econ = 0, grid = 0, seen = false;
      (a.series || []).forEach((r) => {
        if (r.potential_mwh == null) return;
        seen = true;
        pot  += r.potential_mwh;
        econ += r.econ_curtailed_mwh || 0;
        grid += r.grid_curtailed_mwh || 0;
      });
      if (!seen || pot <= 0) return null;
      return {
        assetId: a.asset_id, name: a.name, isTarget: !!a.isTarget,
        econPct: (econ / pot) * 100, gridPct: (grid / pot) * 100,
      };
    })
    .filter(Boolean);

  if (rows.length === 0) return null;
  const maxTotal = Math.max(1, ...rows.map((r) => r.econPct + r.gridPct));

  const LegendDot = ({ color, label }) => (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />
      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{label}</Typography>
    </Stack>
  );

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={2}
             sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography sx={{
          fontSize: '0.66rem', color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
        }}>
          Curtailment cause split · % of potential
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <LegendDot color={ENERGY_PALETTE.econ_curt} label="Economic curtailment" />
        <LegendDot color={ENERGY_PALETTE.grid_curt} label="Grid curtailment" />
      </Stack>
      <Stack spacing={0.75}>
        {rows.map((r) => (
          <Stack key={r.assetId} direction="row" alignItems="center" spacing={1}>
            <Typography noWrap sx={{
              width: 170, flexShrink: 0, fontSize: '0.74rem',
              fontWeight: r.isTarget ? 700 : 400,
              color: r.isTarget ? TARGET_COLOR : 'text.primary',
            }}>
              {r.name}{r.isTarget ? ' ·' : ''}
            </Typography>
            <Box sx={{ flex: 1, height: 14, borderRadius: 0.5,
                       bgcolor: 'action.hover', display: 'flex', overflow: 'hidden' }}>
              <Box sx={{ width: `${(r.econPct / maxTotal) * 100}%`,
                         bgcolor: ENERGY_PALETTE.econ_curt }} />
              <Box sx={{ width: `${(r.gridPct / maxTotal) * 100}%`,
                         bgcolor: ENERGY_PALETTE.grid_curt }} />
            </Box>
            <Typography sx={{
              width: 170, flexShrink: 0, textAlign: 'right',
              fontSize: '0.72rem', fontFamily: "'JetBrains Mono', monospace",
              color: 'text.secondary',
            }}>
              {(r.econPct + r.gridPct).toFixed(1)}%
              <Box component="span" sx={{ color: 'text.disabled' }}>
                {' '}({r.econPct.toFixed(1)} / {r.gridPct.toFixed(1)})
              </Box>
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}


// ── Comparison summary footer ───────────────────────────────────────────
//
// Shown beneath the chart. For the active metric: target value, peer
// average (mean across peers that have data), delta (target − peer avg),
// and rank (1-indexed position of target when ranked best-to-worst on
// the same axis). Higher = better for CF, generation and revenue $/MWh;
// metrics flagged lowerIsBetter (curtailment) flip both the delta
// colour-coding and the rank direction.

function fmtDelta(metric, delta) {
  if (delta == null) return '—';
  const sign = delta >= 0 ? '+' : '−';
  const abs  = Math.abs(delta);
  if (metric.unit === '%') {
    return `${sign}${abs.toFixed(1)} pp`;
  }
  return `${sign}${metric.fmt(abs).replace(/^\$/, '$')}`;
}

function ComparisonSummary({ metric, target, peersWithData }) {
  const tVal = aggregate(target?.series, metric);

  // Aggregate each peer's series with the same agg fn (avg / sum), drop
  // peers that have no data so they don't pull the mean toward zero.
  const peerVals = peersWithData
    .map((p) => aggregate(p.series, metric))
    .filter((v) => v != null);
  const peerAvg = peerVals.length
    ? peerVals.reduce((a, b) => a + b, 0) / peerVals.length
    : null;

  // Rank within the cohort (target + peers with data). 1 = best.
  let rank = null;
  if (tVal != null) {
    const all = [tVal, ...peerVals].sort(
      (a, b) => (metric.lowerIsBetter ? a - b : b - a)
    );
    rank = all.indexOf(tVal) + 1;
  }
  const cohortSize = 1 + peerVals.length;

  const delta = (tVal != null && peerAvg != null) ? tVal - peerAvg : null;
  const deltaPctOfAvg = (delta != null && peerAvg) ? (delta / Math.abs(peerAvg)) * 100 : null;
  const deltaIsGood = metric.lowerIsBetter ? delta <= 0 : delta >= 0;
  const deltaColor = delta == null ? 'text.secondary'
    : deltaIsGood ? 'success.main' : 'error.main';

  return (
    <Box sx={{
      mt: 2, pt: 1.5,
      borderTop: '1px solid', borderColor: 'divider',
      display: 'grid', gap: 1.5,
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
    }}>
      <Stat label={`${target?.name || 'Target'}`} value={metric.fmt(tVal)} accent={TARGET_COLOR} />
      <Stat label={`Peer avg (n=${peerVals.length})`} value={metric.fmt(peerAvg)} />
      <Stat label="Δ vs peer avg" value={fmtDelta(metric, delta)}
            sub={deltaPctOfAvg != null ? `${deltaPctOfAvg >= 0 ? '+' : ''}${deltaPctOfAvg.toFixed(1)}%` : null}
            accent={deltaColor} />
      <Stat label="Rank"
            value={rank != null ? `${rank} / ${cohortSize}` : '—'}
            sub={rank === 1 ? 'top of cohort' : rank === cohortSize ? 'bottom of cohort' : null} />
    </Box>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: '0.66rem', color: 'text.secondary',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        fontWeight: 700, mb: 0.5,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: '1.05rem', fontWeight: 700,
        fontFamily: "'JetBrains Mono', monospace",
        color: accent || 'text.primary',
        lineHeight: 1.1,
      }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}


// ── Page wrapper ─────────────────────────────────────────────────────────

const Benchmarks = () => {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  const [portfolio, setPortfolio] = useState(null);
  // Same persisted picker state as the Technical tabs — pick "90d" there
  // and this page opens on the exact same window (and vice versa).
  const [period, setPeriod] = usePersistedPeriod('lumeros.technical.period', '30d');
  const [error, setError] = useState(null);
  const [seriesAssetId, setSeriesAssetId] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editBenchmarks, setEditBenchmarks] = useState({});
  const [editAssets, setEditAssets] = useState([]);
  const [editAllAssets, setEditAllAssets] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!portfolioId) return;
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}`)
      .then((r) => {
        setPortfolio(r.data);
        const firstAsset = (r.data.assets || [])[0];
        if (firstAsset && !seriesAssetId) setSeriesAssetId(firstAsset.asset_id);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  const handleOpenEdit = async () => {
    setEditOpen(true);
    setEditLoading(true);
    try {
      const [portfolioRes, assetsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}`),
        axios.get(`${API_BASE_URL}/api/portfolios/assets`),
      ]);
      const pf = portfolioRes.data;
      const assets = pf.assets || [];
      setEditAssets(assets);
      setEditAllAssets(assetsRes.data.assets || []);
      const seed = {};
      Object.entries(pf.benchmarks || {}).forEach(([aid, list]) => {
        seed[aid] = (list || []).map((b) => ({
          ...b,
          cross_region:
            b.region_id !== assets.find((a) => a.asset_id === aid)?.region_id,
        }));
      });
      setEditBenchmarks(seed);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load benchmarks editor');
      setEditOpen(false);
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    try {
      const benchmarkPayload = {};
      const sourcePayload = {};
      Object.entries(editBenchmarks).forEach(([aid, list]) => {
        if (!list) return;
        benchmarkPayload[aid] = list.map((b) => b.asset_id);
        sourcePayload[aid] = list.map((b) => b.source || 'auto');
      });
      await axios.put(`${API_BASE_URL}/api/portfolios/${portfolioId}/benchmarks`, {
        benchmarks: benchmarkPayload,
        benchmark_sources: sourcePayload,
      });
      setEditOpen(false);
      // Series chart refetches on its own when assetId/period change; no
      // page-level summary fetch to refresh anymore.
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save benchmarks');
    } finally {
      setEditSaving(false);
    }
  };

  if (error && !portfolio) {
    return <Box sx={{ p: 2.5 }}><Alert severity="error">{error}</Alert></Box>;
  }

  const assets = portfolio?.assets || [];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      {/* Header strip */}
      <Stack direction="row" alignItems="center" spacing={2}
             sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <CompareArrowsIcon sx={{ fontSize: 22, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          Benchmarks
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {/* Asset picker only shown when there are multiple assets to
            choose between — hide it on single-asset portfolios. */}
        {assets.length > 1 && (
          <Select size="small" value={seriesAssetId || ''}
                  onChange={(e) => setSeriesAssetId(e.target.value)}
                  sx={{ minWidth: 220 }}>
            {assets.map((a) => (
              <MenuItem key={a.asset_id} value={a.asset_id}>
                {a.name} ({a.asset_id})
              </MenuItem>
            ))}
          </Select>
        )}
        <PeriodPicker value={period} onChange={setPeriod}
                      showFrequency={false} size="small" />
        <Button
          size="small"
          startIcon={<EditIcon fontSize="small" />}
          onClick={handleOpenEdit}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Edit benchmarks
        </Button>
      </Stack>

      {/* Time-series chart for picked asset (CF / Generation / Capture
          / Revenue). The bars summary section was removed — CF lives as
          the first metric card on the chart now. */}
      {assets.length > 0 && (
        <SeriesSection
          portfolioId={portfolioId}
          assetId={seriesAssetId}
          period={period}
          colors={colors}
          targetFuel={assets.find((a) => a.asset_id === seriesAssetId)?.fuel_code}
        />
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit benchmarks{portfolio?.name ? ` · ${portfolio.name}` : ''}</DialogTitle>
        <DialogContent dividers>
          {editLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Skeleton variant="rectangular" height={300} />
            </Box>
          ) : (
            <BenchmarkStep
              selectedAssets={editAssets}
              allAssets={editAllAssets}
              benchmarks={editBenchmarks}
              setBenchmarks={setEditBenchmarks}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Benchmarks;
